import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import "@ncdai/react-wheel-picker/style.css";
import { WheelPicker as ReactWheelPicker } from "@ncdai/react-wheel-picker";
import { Button } from "@/components/ui/button";
import { useTimePickerIncrement } from "@/hooks/useTimePickerIncrement";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Custom time picker with scrollable 5-minute increments.
 * Opens as a centered modal with scrollable hour/minute wheels.
 */
export function TimeInputField({
  value,
  onChange,
  className,
  style,
  borderColor,
  leadingIcon,
  onClick,
  use24Hour = false,
}: {
  value: string | null;
  onChange: (value: string) => void;
  className?: string;
  style?: React.CSSProperties;
  borderColor?: string;
  leadingIcon?: ReactNode;
  onClick?: () => void;
  use24Hour?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const formatDisplay = (v: string) => {
    if (!v) return "--:--";
    const [h, m] = v.split(":").map(Number);
    if (use24Hour) return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    const hour12 = h % 12 || 12;
    const ampm = h < 12 ? "AM" : "PM";
    return `${hour12}:${String(m).padStart(2, "0")} ${ampm}`;
  };

  return (
    <div className={`time-picker-input ${className ?? ""}`}>
      <button
        type="button"
        onClick={() => onClick ? onClick() : setIsOpen(true)}
        className="time-picker-input-button"
        style={style}
      >
        {leadingIcon && <span className="time-picker-input-icon" aria-hidden="true">{leadingIcon}</span>}
        <span className="time-picker-input-value">{formatDisplay(value ?? "")}</span>
      </button>

      <TimePickerDialog
        open={isOpen}
        value={value}
        onOpenChange={setIsOpen}
        onChange={onChange}
        accentColor={borderColor}
      />
    </div>
  );
}

export function TimePickerDialog({
  open,
  value,
  onOpenChange,
  onChange,
  accentColor,
}: {
  open: boolean;
  value: string | null;
  onOpenChange: (open: boolean) => void;
  onChange: (value: string) => void;
  accentColor?: string;
}) {
  const [tempTime, setTempTime] = useState("00:00");
  const minuteIncrement = useTimePickerIncrement();

  useEffect(() => {
    if (!open) return;
    setTempTime(value || "00:00");
  }, [open, value]);

  const handleSave = () => {
    onChange(tempTime);
    onOpenChange(false);
  };

  const handleClear = () => {
    onChange("");
    onOpenChange(false);
  };

  const handleNow = () => {
    const now = new Date();
    setTempTime(roundTimeToIncrement(formatTimeValue(now.getHours(), now.getMinutes()), minuteIncrement));
  };

  return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          showCloseButton={false}
          className="z-[100] w-[min(20rem,calc(100%-2rem))] rounded-2xl p-5 shadow-2xl"
        >
          <DialogTitle className="sr-only">Select time</DialogTitle>

            <TimePicker
              value={tempTime}
              onChange={setTempTime}
              accentColor={accentColor}
              minuteIncrement={minuteIncrement}
            />

            <div className="flex justify-between text-m px-6 py-3 border-b border-border/60">
              <button
                type="button"
                onClick={handleClear}
                className="font-medium transition-opacity hover:opacity-60"
                style={{ color: accentColor || "var(--muted-foreground)" }}
              >
                Clear
              </button>
              <button
                type="button"
                onClick={handleNow}
                className="font-medium transition-opacity hover:opacity-60"
                style={{ color: accentColor || "var(--muted-foreground)" }}
              >
                Now
              </button>
            </div>

            <div className="flex gap-3 pt-1">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                className="flex-1 text-white"
                style={{
                  backgroundColor: accentColor || "hsl(var(--primary))",
                  borderColor: accentColor || "hsl(var(--primary))",
                }}
              >
                Save
              </Button>
            </div>
        </DialogContent>
      </Dialog>
  );
}

export function TimePicker({
  value,
  onChange,
  accentColor,
  minuteIncrement = 5,
  onDayOffsetChange,
}: {
  value: string;
  onChange: (value: string) => void;
  accentColor?: string;
  minuteIncrement?: 1 | 5;
  onDayOffsetChange?: (offset: -1 | 1) => void;
}) {
  const roundedValue = roundTimeToIncrement(value, minuteIncrement);
  const roundedParts = parseTimeValue(roundedValue);
  const [wheelHours, setWheelHours] = useState(roundedParts.h);
  const wheelHoursRef = useRef(roundedParts.h);
  const [wheelMinutes, setWheelMinutes] = useState(roundedParts.m);
  const minuteOptions = Array.from({ length: 60 / minuteIncrement }, (_, i) => i * minuteIncrement);
  const hourOptions = Array.from({ length: 24 }, (_, i) => i);

  useEffect(() => {
    const next = parseTimeValue(roundTimeToIncrement(value, minuteIncrement));
    setWheelHours(next.h);
    wheelHoursRef.current = next.h;
    setWheelMinutes(next.m);
  }, [minuteIncrement, value]);

  function commitTypedValue(nextValue: string) {
    onChange(nextValue);
    const nextWheel = parseTimeValue(roundTimeToIncrement(nextValue, minuteIncrement));
    setWheelHours(nextWheel.h);
    wheelHoursRef.current = nextWheel.h;
    setWheelMinutes(nextWheel.m);
  }

  function changeWheelHours(nextHours: number) {
    const dayOffset = wheelDayOffset(wheelHoursRef.current, nextHours);
    wheelHoursRef.current = nextHours;
    setWheelHours(nextHours);
    onChange(formatTimeValue(nextHours, wheelMinutes));
    if (dayOffset) onDayOffsetChange?.(dayOffset);
  }

  function changeWheelMinutes(nextMinutes: number) {
    setWheelMinutes(nextMinutes);
    onChange(formatTimeValue(wheelHours, nextMinutes));
  }

  return (
    <div className="time-picker">
      <TimeValueEditor value={value} onChange={commitTypedValue} accentColor={accentColor} />
      <div className="wheels">
        <WheelPicker
          options={hourOptions}
          value={wheelHours}
          onChange={changeWheelHours}
          format={v => String(v).padStart(2, "0")}
          accentColor={accentColor}
        />

        <span className="separator">:</span>

        <WheelPicker
          options={minuteOptions}
          value={wheelMinutes}
          onChange={changeWheelMinutes}
          format={v => String(v).padStart(2, "0")}
          accentColor={accentColor}
        />
      </div>
    </div>
  );
}

export function wheelDayOffset(previousHour: number, nextHour: number): -1 | 0 | 1 {
  // Momentum can skip intermediate values, so detect the boundary zone rather
  // than requiring an exact 23 -> 00 callback sequence.
  if (previousHour >= 18 && nextHour <= 5) return 1;
  if (previousHour <= 5 && nextHour >= 18) return -1;
  return 0;
}

function TimeValueEditor({ value, onChange, accentColor }: { value: string; onChange: (value: string) => void; accentColor?: string }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  function beginEditing() {
    setDraft(value.replace(":", ""));
    setEditing(true);
  }

  function finishEditing() {
    const normalized = normalizeNumericTimeInput(draft);
    if (normalized) onChange(normalized);
    setEditing(false);
  }

  if (editing) {
    return (
      <input
        autoFocus
        className="time-value-input"
        aria-label="Enter time in 24-hour format"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={4}
        value={draft}
        onFocus={event => event.currentTarget.select()}
        onChange={event => setDraft(event.currentTarget.value.replace(/\D/g, "").slice(0, 4))}
        onBlur={finishEditing}
        onKeyDown={event => {
          if (event.key === "Enter") finishEditing();
          if (event.key === "Escape") setEditing(false);
        }}
        style={{ color: accentColor }}
      />
    );
  }

  return (
    <button type="button" className="time-value-display" onClick={beginEditing} style={{ color: accentColor }}>
      {formatTime12Hour(value)}
    </button>
  );
}

export function normalizeNumericTimeInput(rawValue: string): string {
  const digits = rawValue.replace(/\D/g, "");
  if (!digits) return "";
  const clockNumber = Math.min(Number(digits), 2400);
  const hours = Math.floor(clockNumber / 100);
  const minutes = clockNumber % 100;
  const totalMinutes = (hours * 60 + minutes) % (24 * 60);
  return formatTimeValue(Math.floor(totalMinutes / 60), totalMinutes % 60);
}

export function roundTimeToIncrement(value: string, increment: 1 | 5): string {
  const { h, m } = parseTimeValue(value);
  const roundedTotal = Math.round((h * 60 + m) / increment) * increment % (24 * 60);
  return formatTimeValue(Math.floor(roundedTotal / 60), roundedTotal % 60);
}

export function formatTime12Hour(value: string): string {
  const { h, m } = parseTimeValue(value);
  const suffix = h < 12 ? "AM" : "PM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${suffix}`;
}

export function parseTimeValue(value: string): { h: number; m: number } {
  if (!value) return { h: 0, m: 0 };
  const [h, m] = value.split(":").map(Number);
  return { h: h || 0, m: m || 0 };
}

export function formatTimeValue(hours: number, minutes: number): string {
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

export function roundedNowTimeParts(): { h: number; m: number } {
  const now = new Date();
  return {
    h: now.getHours(),
    m: Math.round(now.getMinutes() / 5) * 5 % 60,
  };
}

function WheelPicker({
  options,
  value,
  onChange,
  format,
  accentColor,
}: {
  options: number[];
  value: number;
  onChange: (v: number) => void;
  format: (v: number) => string;
  accentColor?: string;
}) {
  const ITEM_HEIGHT = 44;
  const wheelOptions = useMemo(
    () =>
      options.map(opt => ({
        value: opt,
        label: (
          <span className="font-mono text-lg">
            {format(opt)}
          </span>
        ),
        textValue: format(opt),
      })),
    [format, options],
  );

  return (
    <div className="relative w-16 overflow-hidden rounded-lg bg-card border border-border">
      <div
        className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-11 pointer-events-none z-10 rounded-md"
        style={{
          background: accentColor
            ? `color-mix(in oklch, ${accentColor} 15%, transparent)`
            : "color-mix(in oklch, var(--primary) 12%, transparent)",
          borderTop: `1px solid ${accentColor || "var(--border)"}`,
          borderBottom: `1px solid ${accentColor || "var(--border)"}`,
        }}
      />
      <div className="absolute top-0 left-0 right-0 h-10 bg-gradient-to-b from-card to-transparent pointer-events-none z-10" />
      <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-card to-transparent pointer-events-none z-10" />

      <ReactWheelPicker<number>
        value={value}
        onValueChange={onChange}
        options={wheelOptions}
        infinite
        visibleCount={12}
        optionItemHeight={ITEM_HEIGHT}
        dragSensitivity={3}
        scrollSensitivity={5}
        classNames={{
          optionItem: "text-muted-foreground",
          highlightWrapper: "z-20",
          highlightItem: "font-bold",
        }}
      />
    </div>
  );
}
